import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, Mail, MessageCircle, MapPin } from "lucide-react";
import LegalLayout from "../components/LegalLayout";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import {
  CONTACT_ADDRESS,
  CONTACT_EMAIL,
  CONTACT_WHATSAPP_NUMBER,
  whatsappHref,
  mailtoHref,
} from "../config/contact";

/**
 * Public contact page. Form saves to the backend `requests` collection with
 * type="contact" so the AUTEXIO team can process incoming messages via the
 * admin CRM. NO email forwarding — deferred until the domain is live.
 */
export default function ContattiPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      await api.post("/public/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setSent(true);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout
      testid="contatti-page"
      overline={t("contact.overline")}
      title={t("contact.title")}
    >
      <p className="text-slate-500 -mt-4 mb-10 max-w-2xl">{t("contact.subtitle")}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left: form */}
        <div>
          {sent ? (
            <div
              data-testid="contact-form-success"
              className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center"
            >
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="mt-4 font-heading font-semibold text-lg text-slate-950">
                {t("contact.success_title")}
              </h3>
              <p className="mt-2 text-sm text-emerald-900/80">{t("contact.success_body")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label htmlFor="c-name" className="text-xs font-medium text-slate-600">
                  {t("contact.field_name")}
                </Label>
                <Input
                  id="c-name"
                  required
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder=""
                  className="mt-1 h-11 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="c-email" className="text-xs font-medium text-slate-600">
                  {t("contact.field_email")}
                </Label>
                <Input
                  id="c-email"
                  type="email"
                  required
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder=""
                  className="mt-1 h-11 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="c-message" className="text-xs font-medium text-slate-600">
                  {t("contact.field_message")}
                </Label>
                <Textarea
                  id="c-message"
                  required
                  rows={5}
                  minLength={10}
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              {err && (
                <div
                  data-testid="contact-error"
                  className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                >
                  {err}
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t("contact.sending")}
                  </>
                ) : (
                  t("contact.submit")
                )}
              </Button>
              <p className="text-[11px] text-slate-400 leading-snug">
                {t("contact.privacy_note")}
              </p>
            </form>
          )}
        </div>

        {/* Right: direct contact channels */}
        <aside className="rounded-2xl bg-slate-50 border border-slate-100 p-6 space-y-5">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
            {t("contact.direct_overline")}
          </p>

          <ChannelRow
            icon={<Mail className="w-4 h-4 text-blue-600" />}
            label={t("contact.email_label")}
            testid="contact-info-email"
            href={mailtoHref(CONTACT_EMAIL)}
            value={CONTACT_EMAIL}
          />

          <ChannelRow
            icon={<MessageCircle className="w-4 h-4 text-emerald-600" />}
            label={t("contact.whatsapp_label")}
            testid="contact-info-whatsapp"
            href={whatsappHref(CONTACT_WHATSAPP_NUMBER)}
            value={CONTACT_WHATSAPP_NUMBER}
            hint={t("contact.whatsapp_hint")}
          />

          <a
            href={whatsappHref(CONTACT_WHATSAPP_NUMBER)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-cta"
            className="inline-flex items-center justify-center gap-2 w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            {t("contact.whatsapp_cta")}
          </a>

          <ChannelRow
            icon={<MapPin className="w-4 h-4 text-slate-500" />}
            label={t("contact.address_label")}
            testid="contact-info-address"
            value={
              <span>
                {CONTACT_ADDRESS.company}
                <br />
                {CONTACT_ADDRESS.street}
                <br />
                {CONTACT_ADDRESS.city}
                <br />
                {CONTACT_ADDRESS.country}
              </span>
            }
          />
        </aside>
      </div>
    </LegalLayout>
  );
}

function ChannelRow({ icon, label, value, href, hint, testid }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-sm text-slate-900 leading-snug" data-testid={testid}>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-900 hover:text-blue-600 transition-colors font-medium"
          >
            {value}
          </a>
        ) : (
          value
        )}
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-0.5 italic">{hint}</div>}
    </div>
  );
}
